// Validación de formularios del módulo de cobranza — CR-E1-006.
//
// SPEC RN-3, RN-4, RN-38 · pedido del usuario (2026-08-31)
//
// **Nota sobre la dependencia.** El repo tiene una convención escrita de "sin
// librería nueva salvo imprescindible" (`scripts/migrate.js`,
// `jobSchedulerService.js`, `PLAN_MIGRACIONES_SEED_SEGUROS_v0.1.md`). `zod` la
// rompe a propósito, por pedido explícito: acá el valor es concreto —un
// esquema declarativo que produce **un mensaje por campo y la ruta del campo
// que falló**, que es justo lo que hace falta para llevar el foco al control
// equivocado—. Escribir eso a mano son cien líneas de `if` que se
// desincronizan del backend a la primera.
//
// Se confina al frontend. El backend sigue validando por su cuenta: esto
// **adelanta** el rechazo para que el operador no cargue todo antes de
// enterarse, no lo reemplaza. La base es la que garantiza (PLAN 6).

import { z } from 'zod';

// Espejo de `domain/cobros/ficha.js` en el backend.
const SNAKE_CASE = /^[a-z][a-z0-9_]*$/;

const textoRequerido = (campo) =>
  z.string({ error: `Completá ${campo}.` })
    .trim()
    .min(1, `Completá ${campo}.`);

export const fichaSchema = z.object({
  nombre: textoRequerido('el nombre'),
  descripcion: textoRequerido('qué es'),
  id: textoRequerido('el identificador'),
  detalle: z.array(
    z.object({
      clave: z.string().trim(),
      valor: z.string().trim()
    })
  ).default([])
}).superRefine((ficha, ctx) => {
  ficha.detalle.forEach((campo, i) => {
    // Una fila totalmente vacía es la fila en blanco que la UI ofrece por
    // comodidad: no es un error, se descarta al enviar.
    if (!campo.clave && !campo.valor) return;

    if (!campo.clave) {
      ctx.addIssue({
        code: 'custom',
        path: ['detalle', i, 'clave'],
        message: 'Falta el nombre del campo.'
      });
    } else if (!SNAKE_CASE.test(campo.clave)) {
      ctx.addIssue({
        code: 'custom',
        path: ['detalle', i, 'clave'],
        message: `"${campo.clave}" va en minúsculas y con guion bajo. Ej.: ${sugerirSnake(campo.clave)}`
      });
    }

    if (campo.clave && !campo.valor) {
      ctx.addIssue({
        code: 'custom',
        path: ['detalle', i, 'valor'],
        message: 'Falta el valor.'
      });
    }
  });
});

export const titularSchema = z.object({
  cliente_id: z.number().int().positive(),
  rol: z.enum(['RESPONSABLE_PAGO', 'COTITULAR', 'CONTACTO'])
});

// Reglas de titularidad, en su propio esquema y no como refinamiento del
// objeto grande. El motivo es concreto: en zod, un `superRefine` sobre un
// objeto **no corre si alguno de sus campos falló**. Con la ficha vacía, la
// regla del responsable de pago nunca se evaluaba y el operador arreglaba la
// ficha para recién entonces enterarse de que además le faltaba el titular.
//
// Cada sección se valida por separado y los errores se juntan, así el
// formulario informa TODO lo que falta de una sola vez.
export const titularesSchema = z.array(titularSchema).superRefine((titulares, ctx) => {
  const responsables = titulares.filter((t) => t.rol === 'RESPONSABLE_PAGO');

  if (responsables.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [],
      message: 'Falta el responsable de pago: es quien recibe el documento.'
    });
  } else if (responsables.length > 1) {
    // D3: el reparto entre varios responsables está diferido.
    ctx.addIssue({
      code: 'custom',
      path: [],
      message: 'Sólo puede haber un responsable de pago. Cambiá el rol de los demás a cotitular.'
    });
  }
});

export const cabeceraSchema = z.object({
  cr_esquema_id: z.number({ error: 'Elegí un esquema de cobro.' })
    .int().positive('Elegí un esquema de cobro.'),
  vigencia_desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Indicá desde cuándo rige.'),
  documento_preferido: z.enum(['FACTURA', 'RECIBO'])
});

// Valida el alta completa y devuelve `{ "ruta.del.campo": "mensaje" }` con
// TODOS los problemas, vengan de la sección que vengan.
export function validarAlta(datos) {
  const errores = {};

  Object.assign(errores, erroresPorCampo(cabeceraSchema.safeParse({
    cr_esquema_id: datos.cr_esquema_id,
    vigencia_desde: datos.vigencia_desde,
    documento_preferido: datos.documento_preferido
  })));

  if (datos.usarFicha) {
    const deFicha = erroresPorCampo(fichaSchema.safeParse(datos.ficha || {}));
    for (const [clave, mensaje] of Object.entries(deFicha)) {
      errores[`ficha.${clave}`] = mensaje;
    }
  }

  const deTitulares = titularesSchema.safeParse(datos.titulares || []);
  if (!deTitulares.success) {
    const issue = deTitulares.error.issues[0];
    errores.titulares = issue.message;
  }

  return errores;
}

// Convierte una clave escrita "a mano" en su forma válida, para poder
// sugerirla en el mensaje: "Grado Escolar" -> "grado_escolar".
export function sugerirSnake(texto) {
  return String(texto || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'c$1');
}

// Aplana los errores de zod a `{ "ruta.del.campo": "mensaje" }`, que es la
// forma que la vista necesita para pintar el error al lado del control y para
// saber a cuál llevar el foco.
export function erroresPorCampo(resultado) {
  if (resultado.success) return {};
  const mapa = {};
  for (const issue of resultado.error.issues) {
    const clave = issue.path.join('.');
    // Se conserva el PRIMER error de cada campo: mostrar tres mensajes
    // apilados sobre el mismo input no ayuda a nadie.
    if (!(clave in mapa)) mapa[clave] = issue.message;
  }
  return mapa;
}

// El primer campo con error, en el orden en que el formulario se lee de arriba
// hacia abajo — no en el orden en que zod los reporta.
export function primerCampoConError(errores, orden) {
  return orden.find((campo) => errores[campo]) || Object.keys(errores)[0] || null;
}

// ---------------------------------------------------------------------------
// Etapa E2 — esquemas, conceptos y descuentos
//
// SPEC RN-8, RN-9, RN-13 a RN-16 · D19
//
// Mismo criterio que el alta de suscripción: **un esquema por sección y los
// errores se juntan**. Acá el motivo es todavía más fuerte, porque el esquema
// de cobro tiene dos modos excluyentes y validar el modo equivocado produce
// mensajes que no le dicen nada al operador.
// ---------------------------------------------------------------------------

const enteroPositivo = (campo) =>
  z.number({ error: `Completá ${campo}.` })
    .int(`${campo} tiene que ser un número entero.`)
    .positive(`${campo} tiene que ser mayor a cero.`);

// Lo común a los dos modos. `periodo_inicio` NO va acá: su formato depende del
// modo (AAAA-MM contra AAAA-MM-DD), que es exactamente el `CHECK`
// `cr_esquemas_periodo_inicio_formato` de la migración.
const esquemaBaseSchema = z.object({
  nombre: textoRequerido('el nombre del esquema'),
  cantidad_periodos: enteroPositivo('la cantidad de períodos')
});

const esquemaCalendarioSchema = z.object({
  periodicidad_preset: z.enum(
    ['MENSUAL', 'BIMESTRAL', 'TRIMESTRAL', 'SEMESTRAL', 'ANUAL'],
    { error: 'Elegí cada cuánto se cobra.' }
  ),
  dia_vencimiento: z.number({ error: 'Indicá el día de vencimiento.' })
    .int('El día de vencimiento tiene que ser un número entero.')
    .min(1, 'El día de vencimiento va del 1 al 31.')
    .max(31, 'El día de vencimiento va del 1 al 31.'),
  periodo_inicio: z.string()
    .regex(/^\d{4}-\d{2}$/, 'El primer período va como AAAA-MM. Ej.: 2026-03')
});

const esquemaDiasSchema = z.object({
  periodicidad_dias: enteroPositivo('cada cuántos días se cobra'),
  dias_hasta_vencimiento: z.number({ error: 'Indicá a los cuántos días vence.' })
    .int('Los días hasta el vencimiento tienen que ser un número entero.')
    // Cero es válido: hay ciclos que vencen el mismo día en que arrancan.
    .min(0, 'Los días hasta el vencimiento no pueden ser negativos.'),
  periodo_inicio: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'El primer período va como AAAA-MM-DD. Ej.: 2026-03-01')
});

export function validarEsquema(datos = {}) {
  const errores = erroresPorCampo(esquemaBaseSchema.safeParse({
    nombre: datos.nombre,
    cantidad_periodos: datos.cantidad_periodos ?? undefined
  }));

  // Sólo se valida el modo elegido. Pedirle a alguien que configura por días
  // que además complete el día de vencimiento del mes sería reclamarle un
  // campo que su esquema no tiene.
  const porModo = datos.periodicidad_tipo === 'DIAS'
    ? esquemaDiasSchema.safeParse({
        periodicidad_dias: datos.periodicidad_dias ?? undefined,
        dias_hasta_vencimiento: datos.dias_hasta_vencimiento ?? undefined,
        periodo_inicio: datos.periodo_inicio || ''
      })
    : esquemaCalendarioSchema.safeParse({
        periodicidad_preset: datos.periodicidad_preset,
        dia_vencimiento: datos.dia_vencimiento ?? undefined,
        periodo_inicio: datos.periodo_inicio || ''
      });

  return { ...errores, ...erroresPorCampo(porModo) };
}

const conceptoSchema = z.object({
  descripcion: textoRequerido('la descripción del concepto'),
  tipo: z.enum(['UNICO', 'RECURRENTE'], { error: 'Elegí si el concepto es recurrente o único.' }),
  // Cero es válido: un concepto informativo o bonificado al 100% existe.
  monto: z.number({ error: 'Indicá el monto.' }).min(0, 'El monto no puede ser negativo.'),
  tipo_iva: z.number({ error: 'Elegí el tipo de IVA.' }).int().min(1).max(3)
});

export function validarConcepto(datos = {}) {
  return erroresPorCampo(conceptoSchema.safeParse({
    descripcion: datos.descripcion,
    tipo: datos.tipo,
    monto: datos.monto ?? undefined,
    tipo_iva: datos.tipo_iva ?? undefined
  }));
}

const descuentoSchema = z.object({
  tipo: z.enum(['PORCENTAJE', 'MONTO_FIJO'], { error: 'Elegí porcentaje o monto fijo.' }),
  valor: z.number({ error: 'Indicá el valor del descuento.' })
    .positive('El valor tiene que ser mayor a cero.'),
  // El motivo no es decorativo: es lo que explica, seis meses después, por qué
  // esta persona paga menos (RN-14).
  motivo: textoRequerido('el motivo'),
  vigencia_desde: z.string().optional(),
  vigencia_hasta: z.string().optional()
}).superRefine((d, ctx) => {
  if (d.tipo === 'PORCENTAJE' && d.valor > 100) {
    ctx.addIssue({ code: 'custom', path: ['valor'], message: 'Un porcentaje no puede pasar de 100.' });
  }
  if (d.vigencia_desde && d.vigencia_hasta && d.vigencia_hasta < d.vigencia_desde) {
    ctx.addIssue({
      code: 'custom',
      path: ['vigencia_hasta'],
      message: 'La vigencia no puede terminar antes de empezar.'
    });
  }
});

// `vigentes` son los descuentos que la suscripción ya tiene. Se pasan para
// poder anticipar el tope de RN-15 —la suma de los porcentajes— antes de que
// el backend lo rechace. **La garantía sigue siendo el backend**: esto sólo
// evita que el operador cargue el formulario entero para enterarse ahí.
export function validarDescuento(datos = {}, { vigentes = [] } = {}) {
  const errores = erroresPorCampo(descuentoSchema.safeParse({
    tipo: datos.tipo,
    valor: datos.valor ?? undefined,
    motivo: datos.motivo,
    vigencia_desde: datos.vigencia_desde || undefined,
    vigencia_hasta: datos.vigencia_hasta || undefined
  }));

  if (!errores.valor && datos.tipo === 'PORCENTAJE') {
    const suma = vigentes
      .filter((d) => d.tipo === 'PORCENTAJE')
      .reduce((acc, d) => acc + Number(d.valor), 0) + Number(datos.valor);
    if (suma > 100) {
      errores.valor = `Con los descuentos que ya tiene sumaría ${suma}%, que supera el monto del concepto.`;
    }
  }

  return errores;
}
